import { getLoadTestsConfig } from '@m43/core';
import { UnitGpsDto } from '@mark43/cad-api';
import { useEffect } from 'react';
import { isValidLatLng } from '../../location/helpers';
import { EsriMapStore } from '../stores/esri-map-store';
/**
 * in browser Console run
 *
 *  enableDevSettings = true;
 *  devSettings.loadTests.map = { units: 40, interval: 5 };
 *  OR
 *  devSettings.loadTests.map = { units: 40, events: 10, stations: 5, interval: 5, center: { latitude: 40.7128, longitude: -74.006}, radius: 10 };
 *
 * to enabled map load test with 40 mocked units (each unit has 2 associated users, so total of 40 units + 40 user1 will have random gps updates, 40 user2 will stay on same location),
 * gps/event update interval = 5 seconds.
 * 'center' and 'radius' are optional. By default all mocked units/users will be located around the department center. If center is provided, all units/users will be moving around the 'center', at the distance of 'radius' (in miles).
 * if 'events' and 'stations' are provided, x number of mocked events and stations will be generated near the 'center' or department center
 * The simulator function will be called every 'interval' seconds, but the each gps update call is distributed uniformally within the 'interval'.
 *
 */
export const useEsriMapLoadTestSimulator = (
    mapStore: EsriMapStore,
    addToBulkGpsUpdate: (gps: UnitGpsDto) => void
) => {
    const loadTestsMapConfig = getLoadTestsConfig('map');

    const interval = loadTestsMapConfig?.interval ?? 0;
    const mapCenter = isValidLatLng(
        loadTestsMapConfig?.center?.latitude,
        loadTestsMapConfig?.center?.longitude
    )
        ? loadTestsMapConfig.center
        : undefined;
    const radiusInMiles = loadTestsMapConfig?.radius;

    useEffect(
        function simulateUnitLocationUpdate() {
            if (interval <= 0) {
                return;
            }
            const intervalId = setInterval(() => {
                const randomBinary = Math.floor(Math.random() * 2); // 0 or 1
                const mapUnits = mapStore.mapViewportUnits;
                const gpsUpdateInterval = Math.floor((interval * 1000) / mapUnits.length);
                // in every `interval` seconds, half mocked units will move anticlockwise based on current unit corrdinate,
                // all mocked users will move clockwise based on current user corrdinate
                mapUnits.forEach((mapUnit, index) => {
                    if (
                        !mapUnit.callSign.includes('MOCKED') // real unit will not be changed
                    ) {
                        return;
                    }
                    const waitForMs = gpsUpdateInterval * (index + 1);

                    // select half of mocked units to update the gps
                    if (mapUnit.entityId % 2 === randomBinary) {
                        if (mapUnit.gpsLocation) {
                            const { latitude, longitude } = generateDirectionalPoint(
                                {
                                    latitude: mapUnit.gpsLocation!.latitude!,
                                    longitude: mapUnit.gpsLocation!.longitude!,
                                },
                                mapCenter ?? mapStore.mapCenter!,
                                'anticlockwise',
                                radiusInMiles
                            );
                            // if there are 50 units and 'interval' = 5 seconds, then addToBulkGpsUpdate will be called every 100 milliseconds
                            waitFor(waitForMs).then(() => {
                                addToBulkGpsUpdate({
                                    unitId: mapUnit.entityId,
                                    lat: latitude,
                                    lng: longitude,
                                    dateTime: new Date().toISOString(),
                                    mph: Math.floor(Math.random() * 80) + 10,
                                    isStale: false,
                                });
                            });
                        }
                    } else {
                        // half of user1s associated to unit will move clockwise based on current user location. all user2s will not move
                        const userLocation = mapUnit.unitUserLocations?.[0];
                        if (userLocation) {
                            const {
                                latitude: user1Lat,
                                longitude: user1Lng,
                            } = generateDirectionalPoint(
                                {
                                    latitude: mapUnit.unitUserLocations![0]!.gpsLocation!.latitude!,
                                    longitude: mapUnit.unitUserLocations![0]!.gpsLocation!
                                        .longitude!,
                                },
                                mapCenter ?? mapStore.mapCenter!,
                                'clockwise',
                                radiusInMiles
                            );
                            waitFor(waitForMs).then(() => {
                                addToBulkGpsUpdate({
                                    userId: userLocation.entityId,
                                    unitId: mapUnit.entityId,
                                    lat: user1Lat,
                                    lng: user1Lng,
                                    dateTime: new Date().toISOString(),
                                    mph: Math.floor(Math.random() * 5) + 1,
                                    isStale: false,
                                });
                            });
                        }
                    }
                });
            }, 1000 * interval);
            return () => clearInterval(intervalId);
        },
        [
            interval,
            mapStore,
            addToBulkGpsUpdate,
            mapStore.phoneCallEntity,
            mapStore.amlCallEntity,
            mapCenter,
            radiusInMiles,
        ]
    );
    useEffect(
        function simulateEventUpdate() {
            if (interval <= 0) {
                return;
            }
            const intervalId = setInterval(() => {
                // update all mocked events' narrative every `interval` seconds
                mapStore.mapLedgerEvents?.forEach((mapLedgerEvent) => {
                    if (mapLedgerEvent.agencyEventNumber?.includes('MOCKED')) {
                        (mapLedgerEvent.narrative = `Simulated narrative ${new Date().toISOString()}`),
                            mapStore.upsertMapLedgerEvents([mapLedgerEvent]);
                    }
                });
            }, 1000 * interval);
            return () => clearInterval(intervalId);
        },
        [interval, mapStore]
    );
};

// helpers. Do not export, for map load test simulation only.

type Coordinate = { latitude: number; longitude: number };
type Direction = 'clockwise' | 'anticlockwise';

function toRadians(degrees: number): number {
    return degrees * (Math.PI / 180);
}

function haversineDistance(point1: Coordinate, point2: Coordinate): number {
    const R = 6371; // Radius of the Earth in kilometers
    const dLat = toRadians(point2.latitude - point1.latitude);
    const dLng = toRadians(point2.longitude - point1.longitude);
    const a =
        Math.sin(dLat / 2) ** 2 +
        Math.cos(toRadians(point1.latitude)) *
            Math.cos(toRadians(point2.latitude)) *
            Math.sin(dLng / 2) ** 2;
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c; // Distance in kilometers
}

// Generate a new point B with directional control. B will be located on a circle centered at O with a radius equal to OA and positioned either clockwise or counterclockwise relative to A.
function generateDirectionalPoint(
    A: Coordinate,
    O: Coordinate,
    direction: Direction = 'anticlockwise',
    radiusInMiles?: number
): Coordinate {
    const { latitude: latitudeA, longitude: longitudeA } = A;
    const { latitude: latitudeO, longitude: longitudeO } = O;

    const radius = radiusInMiles ? radiusInMiles * 1.609344 : haversineDistance(O, A); // if radiusInMiles is not provided, use line OA as radius

    // Angle of point A in polar coordinates relative to O
    const angleA = Math.atan2(latitudeA - latitudeO, longitudeA - longitudeO);

    // Increment angle by a random small angle in the specified direction
    const deltaAngle = Math.random() * (Math.PI / 12); // Random angle from 0 to 15 degrees in radians
    const angleB = direction === 'anticlockwise' ? angleA + deltaAngle : angleA - deltaAngle;

    // Calculate B's coordinates
    const latitudeB = latitudeO + (radius / 111) * Math.sin(angleB);
    const longitudeB =
        longitudeO + (radius / (111 * Math.cos(toRadians(latitudeO)))) * Math.cos(angleB);

    return { latitude: latitudeB, longitude: longitudeB };
}

function waitFor(ms: number) {
    return new Promise((resolve) => setTimeout(resolve, ms));
}
